import { useMemo } from 'react'
import { Yakureki, YakurekiDetail } from '@/shared/types/yakurekiCore'
import type { TranscriptionMode } from '@/shared/apis/auth'

export type ScreenMode =
  | 'loading' // データ読み込み中
  | 'non-nsips-pre-recording' // 非NSIPS連携モード（録音前）
  | 'nsips-pre-recording' // NSIPS連携モード（録音前・streaming の録音中を含む）
  | 'post-recording-confirmation' // 音声録音終了後の内容確認画面

export interface UseScreenModeProps {
  patientDetail?: YakurekiDetail
  selectedPatient?: Yakureki
  patientListLoading: boolean
  patientDetailLoading: boolean
  transcriptionMode: TranscriptionMode
}

export interface UseScreenModeReturn {
  screenMode: ScreenMode
}

/**
 * 画面モード判定専用フック
 * - ローディング状態と患者 status、transcription_mode から画面モードを決定
 *
 * streaming 経路では stream/start 直後に backend が status='recording' に遷移
 * させるが、体感として「録音中 = 録音前画面のまま」であるべきなので、streaming
 * モード時のみ 'recording' を pre-recording 扱いにする。SOAP 生成中を示す
 * 'processing' に遷移して初めて post-recording-confirmation へ切替わる.
 * legacy (batch) 経路では 'recording' 遷移が存在しないため従来通り pending
 * のみが pre-recording とみなされる.
 */
export const useScreenMode = ({
  patientDetail,
  selectedPatient,
  patientListLoading,
  patientDetailLoading,
  transcriptionMode,
}: UseScreenModeProps): UseScreenModeReturn => {
  const screenMode = useMemo((): ScreenMode => {
    if (patientListLoading || patientDetailLoading) {
      return 'loading'
    }

    if (!patientDetail) {
      return 'non-nsips-pre-recording'
    }

    // status は patientDetail を優先する。streaming 経路の `recording → processing →
    // completed` 遷移で、patientList (delta sync) が processing を取りこぼし
    // pending → completed の直行遷移になるケースがあり、patientList を優先すると
    // 'processing' を一度も観測できない (= overlay が出ない / post-recording 画面へ
    // の遷移も遅れる) ため。patientDetail は単一レコード refetch なので processing
    // を確実に拾える.
    const status = patientDetail?.status ?? selectedPatient?.status
    const preRecordingStatuses =
      transcriptionMode === 'streaming'
        ? (['pending', 'recording'] as const)
        : (['pending'] as const)

    if (
      status &&
      !(preRecordingStatuses as ReadonlyArray<string>).includes(status)
    ) {
      return 'post-recording-confirmation'
    }

    return 'nsips-pre-recording'
  }, [
    patientListLoading,
    patientDetailLoading,
    patientDetail,
    selectedPatient,
    transcriptionMode,
  ])

  return {
    screenMode,
  }
}
